// This file should be included as the very first script in the <head> to ensure Sentry captures all errors.
// The user consent for tracking should be handled before including this script.
import * as Sentry from '@sentry/browser';

// Default configuration (fallback)
const defaultOptions = {
    integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.browserProfilingIntegration(),
        Sentry.consoleLoggingIntegration({levels: ['log', 'error', 'warn']}),
    ],
    tracesSampleRate: 0.0,
    tracePropagationTargets: [
        "localhost",
        /^https:\/\/.*\.ddev\.site\//,
        /^https:\/\/(?!tr\.).*\.wbstraining\.de\//,
    ],
    profilesSampleRate: 0.0,
    attachStacktrace: true,
};

// Read configuration from script data attribute
const sentryConfigurationElement = document.querySelector('script[data-sentry-configuration]');
let sentryOptions = { ...defaultOptions };

if (sentryConfigurationElement instanceof HTMLScriptElement) {
    try {
        const configData = sentryConfigurationElement.dataset.sentryConfiguration;
        if (configData && configData.trim()) {
            const inlineSentryOptions = JSON.parse(configData);

            // Set DSN from backend configuration (required)
            if (inlineSentryOptions.dsn) {
                sentryOptions.dsn = inlineSentryOptions.dsn;
            }

            // Override environment if provided
            if (inlineSentryOptions.environment) {
                sentryOptions.environment = inlineSentryOptions.environment;
            }

            // Override release if provided
            if (inlineSentryOptions.release) {
                sentryOptions.release = inlineSentryOptions.release;
            }

            // Update sample rates with validation
            const tracesSampleRate = parseFloat(inlineSentryOptions.tracesSampleRate);
            if (!isNaN(tracesSampleRate) && tracesSampleRate >= 0 && tracesSampleRate <= 1) {
                sentryOptions.tracesSampleRate = tracesSampleRate;
            }

            const profilesSampleRate = parseFloat(inlineSentryOptions.profilesSampleRate);
            if (!isNaN(profilesSampleRate) && profilesSampleRate >= 0 && profilesSampleRate <= 1) {
                sentryOptions.profilesSampleRate = profilesSampleRate;
            }

            // Override tracePropagationTargets from config
            // Config provides the current site base URL, we merge it with default patterns
            if (Array.isArray(inlineSentryOptions.tracePropagationTargets)) {
                const configTargets = inlineSentryOptions.tracePropagationTargets
                    .filter(target => target && target.trim().length > 0);

                if (configTargets.length > 0) {
                    // Merge configured targets (strings) with default patterns (regexes)
                    sentryOptions.tracePropagationTargets = [
                        ...defaultOptions.tracePropagationTargets,
                        ...configTargets
                    ];
                }
            }
        }
    } catch (e) {
        console.error('Failed to parse inline Sentry configuration JSON:', e);
    }
}

// Only initialize Sentry if DSN is configured
if (sentryOptions.dsn) {
    Sentry.init(sentryOptions);
    // Expose Sentry to global scope for debugging and manual error capture
    window.Sentry = Sentry;
} else {
    console.warn('Sentry not initialized: SENTRY_FRONTEND_DSN environment variable is not set.');
}
